import { type DragEvent, forwardRef, type MouseEvent, type Ref, useId, useRef, useState } from 'react';

import { Button } from 'components/Button';
import { PlusIcon, UploadSimpleIcon, WarningCircleIcon } from 'components/Icon';
import { InfoButton } from 'components/InfoButton';
import { cn } from 'components/utils';

import {
  fileUploadBadgeRecipe,
  fileUploadDescriptionRecipe,
  fileUploadErrorRecipe,
  fileUploadInputRecipe,
  fileUploadRootRecipe,
  fileUploadTextRecipe,
  fileUploadTileIconRecipe,
  fileUploadTitleRecipe,
  fileUploadTitleRowRecipe,
} from './FileUpload.recipe';
import { selectFiles } from './selectFiles';

import type { FileUploadProps } from './types';

const carriesFiles = (event: DragEvent) => Array.from(event.dataTransfer.types).includes('Files');

/**
 * Design System 2.0 drop area: where a person adds a file, by dragging it in or pressing Browse.
 *
 * Only the empty half of an upload. Once a file is chosen, render a `FileUploadItem` — in place of
 * this for a single file, or above a `row` zone in a list. The component keeps no files of its own:
 * it validates what arrives against `accept`, `maxSize` and `multiple`, hands the accepted files to
 * `onFilesSelect` and the rest, with reasons, to `onReject`.
 *
 * Validation runs on drop as well as in the picker, because a browser applies `accept` to its own
 * dialog and nothing else.
 */
export const FileUpload = forwardRef<HTMLElement, FileUploadProps>((props, ref) => {
  const {
    accept,
    maxSize,
    multiple,
    onFilesSelect,
    onReject,
    disabled,
    name,
    className,
    'data-testid': testId,
    inputTestId,
  } = props;
  const layout = props.layout ?? 'card';

  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  // dragenter and dragleave fire for every child crossed, so a counter, not a boolean, says when
  // the file has really left the zone.
  const depth = useRef(0);
  const id = useId();

  const deliver = (files: FileList | null) => {
    if (!files?.length) return;
    const { accepted, rejected } = selectFiles(Array.from(files), { accept, maxSize, multiple });
    if (rejected.length) onReject?.(rejected);
    if (accepted.length) onFilesSelect(accepted);
  };

  const openPicker = () => {
    if (!disabled) inputRef.current?.click();
  };

  // A disabled zone still swallows the drop, or the browser would navigate away to the file.
  const dragHandlers = disabled
    ? {
        onDragOver: (event: DragEvent) => {
          event.preventDefault();
          event.dataTransfer.dropEffect = 'none';
        },
        onDrop: (event: DragEvent) => event.preventDefault(),
      }
    : {
        onDragEnter: (event: DragEvent) => {
          if (!carriesFiles(event)) return;
          event.preventDefault();
          depth.current += 1;
          setDragging(true);
        },
        onDragOver: (event: DragEvent) => {
          if (!carriesFiles(event)) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = 'copy';
        },
        onDragLeave: () => {
          depth.current = Math.max(0, depth.current - 1);
          if (depth.current === 0) setDragging(false);
        },
        onDrop: (event: DragEvent) => {
          event.preventDefault();
          depth.current = 0;
          setDragging(false);
          deliver(event.dataTransfer.files);
        },
      };

  const invalid = Boolean(props.error);
  const state = {
    'data-layout': layout,
    'data-dragging': dragging || undefined,
    'data-disabled': disabled || undefined,
    'data-invalid': invalid || undefined,
    'data-testid': testId,
  };

  const input = (
    <input
      ref={inputRef}
      type="file"
      tabIndex={-1}
      aria-hidden
      className={fileUploadInputRecipe()}
      accept={accept}
      multiple={multiple}
      disabled={disabled}
      name={name}
      data-testid={inputTestId}
      onChange={(event) => {
        deliver(event.currentTarget.files);
        // Cleared so picking the same file again, after removing it, still fires a change.
        event.currentTarget.value = '';
      }}
    />
  );

  if (props.layout === 'tile') {
    return (
      <>
        <button
          ref={ref as Ref<HTMLButtonElement>}
          type="button"
          disabled={disabled}
          className={cn(fileUploadRootRecipe({ layout }), className)}
          onClick={openPicker}
          {...state}
          {...dragHandlers}
        >
          <PlusIcon size={20} className={fileUploadTileIconRecipe()} />
          <span className={fileUploadTitleRecipe({ layout })}>{props.title}</span>
        </button>
        {input}
      </>
    );
  }

  const { title, dropTitle, description, browseLabel, browseAppearance, error } = props;
  const info = props.info && props.infoLabel ? { text: props.info, name: props.infoLabel } : null;
  const zoneLayout = layout === 'row' ? 'row' : 'card';

  const titleId = `${id}-title`;
  const descriptionId = description ? `${id}-description` : undefined;
  const errorId = invalid ? `${id}-error` : undefined;
  const describedBy = [titleId, descriptionId, errorId].filter(Boolean).join(' ');

  const browseProps =
    browseAppearance === 'primary'
      ? ({ appearance: 'primary', fill: 'muted' } as const)
      : ({ appearance: 'secondary' } as const);

  // A press anywhere on the zone opens the picker, as a convenience for the pointer. Browse is the
  // real control and the only tab stop, so nothing interactive is nested inside another.
  const handleZoneClick = (event: MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('[data-part=browse], [data-part=info], input')) return;
    openPicker();
  };

  return (
    // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- the keyboard route is the Browse button inside
    <div
      ref={ref as Ref<HTMLDivElement>}
      className={cn(fileUploadRootRecipe({ layout: zoneLayout }), className)}
      onClick={handleZoneClick}
      {...state}
      {...dragHandlers}
    >
      <span className={fileUploadBadgeRecipe()} data-part="badge">
        <UploadSimpleIcon size={20} />
      </span>
      <span className={fileUploadTextRecipe({ layout: zoneLayout })}>
        <span className={fileUploadTitleRowRecipe()}>
          <span id={titleId} className={fileUploadTitleRecipe({ layout: zoneLayout })}>
            {dragging ? dropTitle : title}
          </span>
          {info && (
            <span data-part="info" className="inline-flex">
              <InfoButton aria-label={info.name} label={info.text} disabled={disabled} />
            </span>
          )}
        </span>
        {description && (
          <span id={descriptionId} className={fileUploadDescriptionRecipe()}>
            {description}
          </span>
        )}
        {invalid && (
          <span id={errorId} className={fileUploadErrorRecipe()} aria-live="polite">
            <WarningCircleIcon />
            {error}
          </span>
        )}
      </span>
      <Button
        {...browseProps}
        type="button"
        size="xs"
        disabled={disabled}
        aria-describedby={describedBy}
        onClick={openPicker}
        data-part="browse"
        // The zone draws the halo for it (Figma's Focus cell), so the button's own would be a second ring.
        className="focus-visible:shadow-none"
      >
        {browseLabel}
      </Button>
      {input}
    </div>
  );
});
